import type { GenericEmbed } from '@typeform/embed-react';

import { useAutoAnimate } from '@formkit/auto-animate/react';
import { PopupButton } from '@typeform/embed-react';
import { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { Button, Icon, Illustration, Text } from '~/components';
import { env } from '~/constants/env';

import {
  checklistIllustrationStyle,
  checklistItemLabelStyle,
  checklistItemStyle,
  checklistWrapperStyle,
  contentWrapperStyle,
  kiiaEligibilityTestStyle,
  kiiaEligibilityTestTitleStyle,
  kiiaHiddenButtonStyle,
  kiiaIllustrationStyle,
  sidebarStyle,
} from './AuthLayoutSidebar.css';

type Props = {
  variant: 'checklist' | 'kiia';
};

export const AuthLayoutSidebar = ({ variant }: Props) => {
  const [ref] = useAutoAnimate();

  return (
    <aside className={sidebarStyle}>
      <div className={contentWrapperStyle} ref={ref}>
        {match(variant)
          .with('checklist', () => <Checklist />)
          .with('kiia', () => <Kiia />)
          .exhaustive()}
      </div>
    </aside>
  );
};

const Checklist = () => {
  const { t } = useTranslation(['auth']);

  return (
    <>
      <Illustration className={checklistIllustrationStyle} name="PersonDopper" strokeWidth={1.7} />

      <div className={checklistWrapperStyle}>
        <Text className={checklistItemLabelStyle}>{t('auth:sidebars.checklist.features.title')}</Text>
        {[
          'auth:sidebars.checklist.features.first' as const,
          'auth:sidebars.checklist.features.second' as const,
          'auth:sidebars.checklist.features.third' as const,
        ].map((feature) => (
          <div className={checklistItemStyle} key={feature}>
            <Icon name="Check" size={20} />

            <Text className={checklistItemLabelStyle}>
              <Trans components={{ insuranceFaqLink: <a />, strong: <strong /> }} i18nKey={feature} />
            </Text>
          </div>
        ))}
      </div>
    </>
  );
};

const Kiia = () => {
  const embedRef = useRef<GenericEmbed>(undefined);

  const { t } = useTranslation(['auth']);

  return (
    <div>
      <Illustration className={kiiaIllustrationStyle} name="PersonComputer" strokeWidth={2.1} />

      <div className={kiiaEligibilityTestStyle}>
        <Text className={kiiaEligibilityTestTitleStyle}>{t('auth:sidebars.kiia.eligibilityTest.title')}</Text>

        <Text>{t('auth:sidebars.kiia.eligibilityTest.description')}</Text>

        <Button emphasis="secondary" onClick={() => embedRef.current?.open()}>
          {t('auth:sidebars.kiia.eligibilityTest.button')}
        </Button>
      </div>

      <div className={kiiaHiddenButtonStyle}>
        <PopupButton embedRef={embedRef} enableSandbox={!env.IS_PRODUCTION_ENV} id="dcjBYauQ" />
      </div>
    </div>
  );
};
